import React from 'react';
import { langField } from '../../utils/eventLang';

const HeartIcon = ({ filled }) =>
  filled ? (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 14S2 10 2 6a4 4 0 0 1 6-3.46A4 4 0 0 1 14 6c0 4-6 8-6 8z"/>
    </svg>
  ) : (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M8 14S2 10 2 6a4 4 0 0 1 6-3.46A4 4 0 0 1 14 6c0 4-6 8-6 8z"/>
    </svg>
  );

const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"
    style={{ display: 'inline', verticalAlign: 'middle', flexShrink: 0 }}>
    <rect x="1.5" y="3" width="13" height="11" rx="2" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M1.5 7h13" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"
    style={{ display: 'inline', verticalAlign: 'middle', flexShrink: 0 }}>
    <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.75 4.5 8.5 4.5 8.5S12.5 9.75 12.5 6C12.5 3.515 10.485 1.5 8 1.5z"
      stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="8" cy="6" r="1.75" stroke="currentColor" strokeWidth="1.4"/>
  </svg>
);

function pad(n) { return String(n).padStart(2, '0'); }

function fmtDate(isoStr, lang) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

function fmtTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const h = d.getUTCHours(), m = d.getUTCMinutes();
  if (h === 0 && m === 0) return '';
  return `${pad(h)}:${pad(m)}`;
}

function priceLabel(event, lang) {
  const isZh = lang === 'zh';
  if (event.isFree) return isZh ? '免费' : 'Free';
  if (event.priceMin != null) {
    const cur = (event.priceCurrency || '').toUpperCase();
    const prefix = cur === 'CNY' ? '¥' : '';
    const suffix = cur === 'CNY' ? '' : (cur ? ` ${cur}` : '');
    if (event.priceMax && event.priceMax !== event.priceMin) {
      return `${prefix}${event.priceMin}–${event.priceMax}${suffix}`.trim();
    }
    return `${prefix}${event.priceMin}${suffix}`.trim();
  }
  return null;
}

function heatLabel(score, lang) {
  if (score >= 80) return lang === 'zh' ? '热门' : 'Hot';
  if (score >= 50) return lang === 'zh' ? '上升' : 'Trending';
  return null;
}

function EventCard({ event, lang = 'en', onClick, likeCount = 0, liked = false, onLike, canLike = false }) {
  const title = langField(event, 'title', lang);
  const description = langField(event, 'description', lang);
  const category = langField(event, 'category', lang);
  const locationName = langField(event, 'locationName', lang);
  const locationDistrict = langField(event, 'locationDistrict', lang);

  const startDate = fmtDate(event.startAt, lang);
  const startTime = fmtTime(event.startAt);
  const endDate = fmtDate(event.endAt, lang);
  const price = priceLabel(event, lang);
  const heat = event.heatScore != null && event.heatScore > 0
    ? heatLabel(event.heatScore, lang) : null;
  const showEndDate = endDate && endDate !== startDate;

  return (
    <article className="event-card" onClick={onClick}>
      <div className="event-card-top">
        {category && <span className="event-tag event-tag-category">{category}</span>}
        {heat && <span className="event-tag event-tag-heat">{heat}</span>}
      </div>

      <h3 className="event-card-title">{title}</h3>

      <div className="event-card-meta">
        <span className="event-meta-icon"><CalendarIcon /></span>
        <span>
          {startDate}
          {showEndDate && <> &ndash; {endDate}</>}
          {startTime && <span className="event-time">&nbsp;&nbsp;{startTime}</span>}
        </span>
      </div>

      {(locationName || locationDistrict) && (
        <div className="event-card-meta">
          <span className="event-meta-icon"><PinIcon /></span>
          <span>
            {locationName}
            {locationDistrict && (
              <span className="event-district">&nbsp;· {locationDistrict}</span>
            )}
          </span>
        </div>
      )}

      {description && (
        <p className="event-card-desc" title={description}>{description}</p>
      )}

      <div className="event-card-footer">
        {price && (
          <span className={`event-price-badge${event.isFree ? ' is-free' : ''}`}>
            {price}
          </span>
        )}
        <button
          type="button"
          className={`event-like-btn${liked ? ' liked' : ''}${!canLike ? ' no-auth' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (canLike && onLike) onLike();
          }}
          aria-label={liked ? 'Unlike' : 'Like'}
          title={!canLike ? (lang === 'zh' ? '登录后可点赞' : 'Sign in to like') : undefined}
        >
          <HeartIcon filled={liked} />
          {likeCount > 0 && <span className="event-like-count">{likeCount}</span>}
        </button>
      </div>
    </article>
  );
}

export default EventCard;
